/**
 * Room Background Mock Component
 *
 * Renders a gallery wall layout over a room photograph using pure canvas rendering.
 * All frames are drawn synchronously via shared frame-rendering.js functions.
 *
 * Architecture: Single synchronous render() method called via scheduleRender()
 * (rAF deduplication). Eliminates the SVG + blob URL + async pipeline that
 * caused ghost frames, blank artwork, and flickering room transitions.
 */

// Shared rendering functions from frame-rendering.js (loaded via window global).
// Lazy-resolved to avoid race conditions when both modules load in parallel.
const getFrameRendering = () => window.FrameRendering;
const loadImage = (...args) => getFrameRendering().loadImage(...args);
const getCachedImage = (...args) => getFrameRendering().getCachedImage(...args);
const renderArtPrintFrame = (...args) => getFrameRendering().renderArtPrintFrame(...args);
const renderCanvasProductFrame = (...args) => getFrameRendering().renderCanvasProductFrame(...args);

// Layout data cache (shared across all instances for cart previews)
const layoutCache = new Map();

// Rendered state cache (prevents re-render on cart updates)
// Stores { html, dataAttrs } for each layout+artworks combination
const renderedStateCache = new Map();

// Room fit ratio thresholds for auto-switching rooms
// Ratio = max(layoutWidth/realWallWidth, layoutHeight/realWallHeight)
const ROOM_FIT_MIN_RATIO = 0.25;
const ROOM_FIT_MAX_RATIO = 0.90;

// Mount width varies by frame size:
//   ≤ 10" max dim → 1" (2.5 cm)
//   ≤ 15" max dim → 1.5" (4 cm)
//   ≥ 16" max dim → 2" (5 cm)
function getMountWidthCm(widthInches, heightInches) {
  const maxDim = Math.max(widthInches || 0, heightInches || 0);
  if (maxDim <= 10) return 2.5;
  if (maxDim <= 15) return 4;
  return 5;
}

class RoomBackgroundMock extends HTMLElement {
  /** Fetch single-frame layouts with only the fields needed for room mocks */
  static fetchLayouts() {
    if (!window.__layoutsFetch) {
      window.__layoutsFetch = fetch('/a/gallery-wall/api/layouts?frameCount=1&fields=positions,roomBackgrounds,configurations,configuratorOptions')
        .then(r => r.ok ? r.json() : null);
    }
    return window.__layoutsFetch;
  }

  constructor() {
    super();
    this.canvas = null;
    this.ctx = null;
    this._rafId = null;
    this._roomBgImage = null;
    // Crossfade state
    this._crossfadeFrom = null;
    this._crossfadeAlpha = 1;
    this._crossfadeStart = 0;
    this.resizeObserver = null;
    this.currentZoom = 1.0;
    // Offscreen room background cache (avoids redrawing large image every frame)
    this._bgCache = null;       // OffscreenCanvas or <canvas>
    this._bgCacheKey = null;    // Serialised cache key to detect invalidation
  }

  connectedCallback() {
    // Check if we need to fetch layout data (cart preview mode)
    const layoutHandle = this.dataset.layoutHandle;
    if (layoutHandle && !this.querySelector('.room-background-mock__container')) {
      // Cart preview mode: check cache first to avoid re-fetching on cart updates
      const cacheKey = this.getCacheKey();
      const cachedState = renderedStateCache.get(cacheKey);

      if (cachedState) {
        // Restore from cache - both HTML and data attributes
        this.innerHTML = cachedState.html;
        for (const [key, value] of Object.entries(cachedState.dataAttrs)) {
          if (value !== undefined) {
            this.dataset[key] = value;
          }
        }
        this.style.aspectRatio = cachedState.aspectRatio || '';
        this.parseData();
        this._initCanvas();
        this._loadRoomBackgroundImage();
        this._preloadArtworks();
        this.loadTexture();
        this.setupResizeObserver();
        this.setupContentProtection(); // Watch for content replacement
        this.configChangeHandler = this.handleConfigChange.bind(this);
        document.addEventListener('gallery-wall:config-changed', this.configChangeHandler);
        return;
      }

      // Not in cache, fetch and build
      this.initFromHandle(layoutHandle);
      return;
    }

    // Async PDP mode: fetch room data from Storefront API. Defer the fetch
    // until the element is near the viewport (IntersectionObserver) or the
    // browser goes idle, to keep the gallery API call out of the initial
    // load critical path. The element only ever shows on the "view in room"
    // gallery slide which is below the LCP slide.
    if (this.dataset.fetchRoom) {
      // Idle: prefetch just the room photo and paint it on the slide background +
      // the in-room thumbnail placeholders — off the critical path. This lets the
      // thumbnail show the real room (no grey plain-wall) without waiting for the
      // slide to be approached/tapped; the heavy frame composite stays deferred.
      const idle = window.requestIdleCallback || ((f) => setTimeout(f, 1200));
      idle(() => this._prefetchRoomPhoto());

      let fetched = false;
      const fetchOnce = () => {
        if (fetched) return;
        fetched = true;
        this.initFromFetch();
      };

      if ('IntersectionObserver' in window) {
        // Render the room (canvas) ONLY when the slide is approached — no idle
        // fallback, so the heavy canvas composite never lands during initial
        // load on PDPs where the user never opens the in-room slide.
        const io = new IntersectionObserver((entries) => {
          if (entries.some(e => e.isIntersecting)) {
            io.disconnect();
            fetchOnce();
          }
        }, { rootMargin: '200px' });
        io.observe(this);
      } else {
        fetchOnce();
      }
      return;
    }

    // Standard mode: HTML structure provided by Liquid
    this.parseData();
    this._initCanvas();
    this.setupResizeObserver();
    this._loadRoomBackgroundImage();
    this._preloadArtworks();
    this.loadTexture();

    // Listen for configuration changes from the Svelte app block
    this.configChangeHandler = this.handleConfigChange.bind(this);
    document.addEventListener('gallery-wall:config-changed', this.configChangeHandler);

    // Listen for AI Room Fit personal background
    this.roomFitHandler = this.handleRoomFitBackground.bind(this);
    document.addEventListener('room-fit:background-applied', this.roomFitHandler);
  }

  _initCanvas() {
    this.canvas = this.querySelector('.room-background-mock__canvas');
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d');
    }
  }

  /**
   * Lightweight room-photo prefetch (no canvas, no frame): resolve the room
   * background URL via the cached layout fetch and paint it as a CSS background
   * on the slide and the in-room thumbnail placeholders ([data-room-thumb-photo]).
   * The full canvas composite (with frames) still runs on slide approach and
   * draws over this background.
   */
  async _prefetchRoomPhoto() {
    try {
      const artWidth = parseFloat(this.dataset.artworkWidth) || 24;
      const artHeight = parseFloat(this.dataset.artworkHeight) || 18;
      const orient = artWidth > artHeight ? 'landscape' : artHeight > artWidth ? 'portrait' : 'square';
      const handle = RoomBackgroundMock.ORIENTATION_LAYOUTS[orient];
      const layout = await this.fetchLayout(handle);
      const roomBg = (layout?.roomBackgrounds || []).find((rb) => rb && rb.imageUrl);
      if (!roomBg || !roomBg.imageUrl) return;
      const css = `url("${this.getOptimizedImageUrl(roomBg.imageUrl, 600)}")`;
      const paint = (el) => {
        el.style.backgroundImage = css;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
      };
      // Paint only the in-room thumbnail placeholders. Do NOT paint `this`:
      // the main slide's canvas composite (frame + room) renders on approach,
      // and a CSS background here would persist behind/around the canvas.
      document.querySelectorAll('[data-room-thumb-photo]').forEach(paint);
    } catch (e) {
      /* non-fatal — thumbnail keeps its neutral placeholder */
    }
  }

  _loadRoomBackgroundImage() {
    const url = this.dataset.roomImageUrl;
    if (!url) return;
    loadImage(url).then(img => {
      if (img) {
        this._roomBgImage = img;
        this.scheduleRender();
      } else {
        // Retry once — transient CDN/network errors should not leave the mock blank
        loadImage(url).then(retryImg => {
          if (retryImg) {
            this._roomBgImage = retryImg;
            this.scheduleRender();
          }
        });
      }
    });
  }

  _preloadArtworks() {
    if (!this.artworks) return;
    for (const artwork of this.artworks) {
      if (artwork?.url) {
        loadImage(artwork.url).then(() => this.scheduleRender());
      }
    }
  }

  /**
   * Parse a JSON data attribute, handling potential double-encoding from Liquid's | json filter.
   * If Liquid applies | json to a value that's already a JSON string (e.g. from a metaobject
   * text field), the result is a double-encoded string like '"{\\"x\\":20}"'. A single
   * JSON.parse returns the inner string; we detect that and parse again.
   */
  parseJsonAttr(raw, fallback) {
    if (raw === undefined || raw === null || raw === '') return fallback;
    try {
      let parsed = JSON.parse(raw);
      // If the result is a string, it may be double-encoded — parse once more
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          // Not valid JSON inside the string — return original parsed string
        }
      }
      return parsed;
    } catch {
      return fallback;
    }
  }

  /**
   * Watch for content replacement (DOM morphing) and restore from cache immediately
   * This prevents the flash of placeholder content during cart updates
   */
  setupContentProtection() {
    if (this._contentObserver) {
      this._contentObserver.disconnect();
    }

    const cacheKey = this.getCacheKey();

    this._contentObserver = new MutationObserver((mutations) => {
      // Check if our container was removed (replaced with placeholder)
      if (!this.querySelector('.room-background-mock__container')) {
        const cachedState = renderedStateCache.get(cacheKey);
        if (cachedState) {
          // Disconnect temporarily to avoid infinite loop
          this._contentObserver.disconnect();
          this.innerHTML = cachedState.html;
          for (const [key, value] of Object.entries(cachedState.dataAttrs)) {
            if (value !== undefined) {
              this.dataset[key] = value;
            }
          }
          this.style.aspectRatio = cachedState.aspectRatio || '';
          this.parseData();
          this._initCanvas();
          this.scheduleRender();
          // Re-observe
          this._contentObserver.observe(this, { childList: true, subtree: true });
        }
      }
    });

    this._contentObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * Get cache key for this component instance
   */
  getCacheKey() {
    const layoutHandle = this.dataset.layoutHandle || '';
    const artworkUrls = this.dataset.artworkUrls || '';
    return `${layoutHandle}::${artworkUrls}`;
  }

  /**
   * Initialize from layout handle (cart preview mode)
   * Fetches layout data from API and builds HTML structure dynamically
   */
  async initFromHandle(handle) {
    try {
      const layout = await this.fetchLayout(handle);
      if (!layout) {
        this.renderFallback();
        return;
      }

      // Get room background (prefer roomBackgrounds, fall back to lifestyleMocks)
      const roomBg = layout.roomBackgrounds?.[0] || layout.lifestyleMocks?.[0];
      if (!roomBg?.imageUrl) {
        this.renderFallback();
        return;
      }

      // Parse artwork URLs from data attribute
      const artworkUrlsStr = this.dataset.artworkUrls || '';
      const artworkUrls = artworkUrlsStr.split('|||').filter(url => url && url.trim());

      // Parse position IDs from data attribute (these correspond to artworkUrls by index)
      const positionsStr = this.dataset.positions || '';
      const positionIds = positionsStr.split(',').filter(p => p).map(p => p.trim());

      // Build a map from position ID to artwork URL
      const positionToUrlMap = new Map();
      positionIds.forEach((posId, i) => {
        if (artworkUrls[i]) {
          positionToUrlMap.set(String(posId), artworkUrls[i]);
        }
      });

      // Build artworks array in the same order as layout.positions
      const artworks = (layout.positions || []).map(pos => {
        const posId = String(pos.id ?? pos.position_id ?? '');
        return {
          url: positionToUrlMap.get(posId) || '',
          positionId: posId
        };
      });

      // Set data attributes from fetched layout
      this.dataset.wallArea = JSON.stringify(roomBg.wallArea || { x: 20, y: 20, width: 60, height: 60 });
      this.dataset.positions = JSON.stringify(layout.positions || []);
      this.dataset.artworks = JSON.stringify(artworks);
      this.dataset.squareCrop = JSON.stringify(roomBg.squareCrop || null);
      this.dataset.realWallWidth = String(roomBg.realWallWidth || layout.realWallWidth || 100);
      this.dataset.lightingOpacity = String(roomBg.lightingOpacity || 0);
      this.dataset.lightingBlend = roomBg.lightingBlend || 'multiply';

      // Calculate image aspect ratio
      const imgWidth = roomBg.imageWidth || 1600;
      const imgHeight = roomBg.imageHeight || 1000;
      this.dataset.imgAspect = String(imgWidth / imgHeight);

      // Extract frame configuration from layout configurations
      const configHandle = this.dataset.configHandle;
      const firstPos = (layout.positions || [])[0];
      const frameConfig = this.extractFrameConfig(layout, configHandle, firstPos);
      this.dataset.frameColor = frameConfig.frameColor;
      this.dataset.mouldingTile = frameConfig.mouldingTile || '';
      this.dataset.mouldingWidth = String(frameConfig.mouldingWidth);
      this.dataset.borderWidth = String(frameConfig.borderWidth);
      this.dataset.borderOption = frameConfig.borderOption || 'no-border';
      this.dataset.hasFrame = String(frameConfig.hasFrame);

      // Set room image URL for canvas loading
      this.dataset.roomImageUrl = this.getOptimizedImageUrl(roomBg.imageUrl, 1200);

      // Build the HTML structure
      this.buildHtmlStructure(roomBg, imgWidth, imgHeight);

      // Now initialize normally
      this.parseData();
      this._initCanvas();
      this.setupResizeObserver();

      // Mark for caching after render completes
      this._shouldCacheAfterRender = true;

      this._loadRoomBackgroundImage();
      this._preloadArtworks();
      this.loadTexture();

      // Watch for content replacement after initial render
      this.setupContentProtection();

      this.configChangeHandler = this.handleConfigChange.bind(this);
      document.addEventListener('gallery-wall:config-changed', this.configChangeHandler);
    } catch (error) {
      console.error('[RoomBackgroundMock] Error initializing from handle:', error);
      this.renderFallback();
    }
  }

  /**
   * Fetch layout data from the gallery wall API
   */
  async fetchLayout(handle) {
    if (layoutCache.has(handle)) {
      return layoutCache.get(handle);
    }

    // Check if this layout was prefetched during early page load
    // (inline script in _product-media-gallery.liquid starts the fetch
    //  before A/B test morph, saving ~1-2s of API latency)
    const prefetched = window.__layoutPrefetch?.[handle];
    if (prefetched) {
      try {
        const layout = await prefetched;
        if (layout) {
          layoutCache.set(handle, layout);
          return layout;
        }
      } catch { /* fall through to direct fetch */ }
    }

    try {
      const response = await fetch(`/a/gallery-wall/api/layout/${encodeURIComponent(handle)}`);
      if (!response.ok) {
        console.warn('[RoomBackgroundMock] Failed to fetch layout:', response.status);
        return null;
      }

      const data = await response.json();
      const layout = data.layout || data; // Handle wrapped or unwrapped response
      layoutCache.set(handle, layout);
      return layout;
    } catch (error) {
      console.error('[RoomBackgroundMock] Error fetching layout:', error);
      return null;
    }
  }

  /** Preferred layout handles per artwork orientation. */
  static ORIENTATION_LAYOUTS = {
    landscape: 'gallery-wall-layout-lezzgi6q',
    portrait:  'gallery-wall-layout-9roun4jy',
    square:    'gallery-wall-layout-sfnqu8ka',
  };

  /**
   * Initialize from async fetch (PDP mode for single-print products).
   * Picks the hardcoded layout handle for the artwork orientation and
   * fetches only that layout from the API.
   */
  async initFromFetch() {
    try {
      const artWidth = parseFloat(this.dataset.artworkWidth) || 24;
      const artHeight = parseFloat(this.dataset.artworkHeight) || 18;

      const orient = artWidth > artHeight ? 'landscape'
        : artHeight > artWidth ? 'portrait' : 'square';
      const handle = RoomBackgroundMock.ORIENTATION_LAYOUTS[orient];

      const layout = await this.fetchLayout(handle);
      if (!layout) {
        console.warn('[RoomBackgroundMock] No layout for orientation', orient);
        this.renderFallback();
        return;
      }

      // Collect room backgrounds from this layout
      this.allRoomBackgrounds = [];
      const positions = layout.positions || [];
      for (const rb of (layout.roomBackgrounds || [])) {
        if (rb.imageUrl) {
          this.allRoomBackgrounds.push({ ...rb, _layoutPositions: positions });
        }
      }

      // Restore persisted room selection
      this.currentRoomIndex = 0;
      try {
        const saved = localStorage.getItem('room-background-selection');
        if (saved !== null) {
          const idx = parseInt(saved, 10);
          if (idx >= 0 && idx < this.allRoomBackgrounds.length) {
            this.currentRoomIndex = idx;
          }
        }
      } catch { /* localStorage unavailable */ }

      this._preferredRoomIndex = this.currentRoomIndex;
      this._isAutoSwitchedRoom = false;
      this._layoutHeight = artHeight;
      this.layoutWidth = artWidth;
      this.dataset.layoutWidth = String(artWidth);

      // Pick the best-fitting room for initial artwork dimensions
      const idealRatio = Math.sqrt(ROOM_FIT_MIN_RATIO * ROOM_FIT_MAX_RATIO);
      const longestSide = Math.max(artWidth, artHeight);
      let bestIdx = 0, bestDist = Infinity;
      for (let i = 0; i < this.allRoomBackgrounds.length; i++) {
        const rw = this.allRoomBackgrounds[i].realWallWidth || 100;
        const ratio = longestSide / rw;
        const dist = Math.abs(ratio - idealRatio);
        if (dist < bestDist) { bestDist = dist; bestIdx = i; }
      }
      this.currentRoomIndex = bestIdx;

      const roomBg = this.allRoomBackgrounds[this.currentRoomIndex] || layout.roomBackgrounds?.[0];
      if (!roomBg?.imageUrl) {
        this.renderFallback();
        return;
      }

      // Parse artworks from data attribute
      let artworks;
      try {
        artworks = JSON.parse(this.dataset.artworks || '[]');
      } catch {
        artworks = [];
      }

      // Set data attributes from fetched room data (same as initFromHandle)
      this.dataset.wallArea = JSON.stringify(roomBg.wallArea || { x: 20, y: 20, width: 60, height: 60 });
      this.dataset.positions = this.dataset.positions || JSON.stringify(layout.positions || []);
      this.dataset.artworks = JSON.stringify(artworks);
      this.dataset.squareCrop = JSON.stringify(roomBg.squareCrop || null);
      this.dataset.realWallWidth = String(roomBg.realWallWidth || 100);
      this.dataset.lightingOpacity = String(roomBg.lightingOpacity || 0);
      this.dataset.lightingBlend = roomBg.lightingBlend || 'multiply';

      const imgWidth = roomBg.imageWidth || 1600;
      const imgHeight = roomBg.imageHeight || 1000;
      this.dataset.imgAspect = String(imgWidth / imgHeight);

      // Set room image URL for canvas loading
      this.dataset.roomImageUrl = this.getOptimizedImageUrl(roomBg.imageUrl, 1200);

      // Build the HTML structure
      this.buildHtmlStructure(roomBg, imgWidth, imgHeight);

      // Initialize normally
      this.parseData();
      this._initCanvas();
      this.setupResizeObserver();
      this._loadRoomBackgroundImage();
      this._preloadArtworks();
      this.loadTexture();

      // Set up room picker if multiple rooms available and not disabled
      if (this.allRoomBackgrounds.length > 1 && this.getAttribute('data-enable-room-switcher') !== 'false' && !this.dataset.aiRoomFit) {
        this.setupRoomPicker();
      }

      this.configChangeHandler = this.handleConfigChange.bind(this);
      document.addEventListener('gallery-wall:config-changed', this.configChangeHandler);

      // If sku-picker already broadcast config before we finished loading,
      // replay its config so we render the correct frame from the start.
      const skuPicker = window.skuPickerInstance;
      if (skuPicker?.lastMockConfig) {
        this.handleConfigChange({ detail: skuPicker.lastMockConfig });
      }
      // Replay size dimensions separately (frame config and size must be separate
      // events because handleConfigChange early-returns on auto-room-switch).
      if (skuPicker?.lastSizeConfig) {
        this.handleConfigChange({ detail: skuPicker.lastSizeConfig });
      }

      // Listen for cross-instance room change broadcasts
      this.roomChangeHandler = this.handleRoomChange.bind(this);
      document.addEventListener('room-background:changed', this.roomChangeHandler);

      // Listen for Statement Pair diptych mode
      this._statementPairHandler = this.handleStatementPairToggle.bind(this);
      document.addEventListener('statement-pair:toggled', this._statementPairHandler);

      // Cache similar art images when related products load
      this._relatedProductsHandler = this._cacheRelatedProducts.bind(this);
      document.addEventListener('related-products:loaded', this._relatedProductsHandler);

      // Notify media-gallery that room background is ready (for thumbnail sync)
      document.dispatchEvent(new CustomEvent('room-background:ready', {
        detail: { roomBg, source: this },
      }));
    } catch (error) {
      console.error('[RoomBackgroundMock] Error initializing from fetch:', error);
      this.renderFallback();
    }
  }

  /**
   * Fetch single-frame room layouts from the gallery-wall app proxy.
   * The API is filtered server-side to frameCount=1 with only the fields we need.
   */
  async fetchRoomLayouts() {
    try {
      const data = await RoomBackgroundMock.fetchLayouts();
      if (!data) {
        console.warn('[RoomBackgroundMock] Failed to fetch layouts');
        return [];
      }
      const layouts = data.layouts || data;
      if (!Array.isArray(layouts)) return [];

      // Filter to layouts that have room background images
      return layouts.filter(l =>
        l.roomBackgrounds?.length > 0 &&
        l.roomBackgrounds[0].imageUrl
      );
    } catch (error) {
      console.error('[RoomBackgroundMock] Error fetching layouts:', error);
      return [];
    }
  }

  /**
   * Match a layout to artwork dimensions by orientation.
   */
  matchLayout(layouts, artWidth, artHeight) {
    const artIsLandscape = artWidth > artHeight;
    const artIsPortrait = artHeight > artWidth;

    for (const layout of layouts) {
      const pos = layout.positions?.[0];
      if (!pos) continue;
      const posW = parseFloat(pos.width) || 1;
      const posH = parseFloat(pos.height) || 1;
      const posIsLandscape = posW > posH;
      const posIsPortrait = posH > posW;

      if (artIsLandscape && posIsLandscape) return layout;
      if (artIsPortrait && posIsPortrait) return layout;
      if (!artIsLandscape && !artIsPortrait) return layout;
    }

    return layouts[0] || null;
  }

  /**
   * Get effective square crop parameters normalised to 0-100.
   */
  getEffectiveCrop() {
    const crop = this.squareCrop;
    if (crop) {
      let { centerX = 0.5, centerY = 0.5, size = 1 } = crop;
      if (centerX <= 1) centerX *= 100;
      if (centerY <= 1) centerY *= 100;
      if (size <= 1) size *= 100;
      return { centerX, centerY, size };
    }
    return { centerX: 50, centerY: 50, size: 100 };
  }

  /**
   * Set up room background picker — FAB + expanding arc of room thumbnails.
   */
  setupRoomPicker() {
    const SVG_ROOM = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
    const SVG_CLOSE = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

    const pickerContainer = document.createElement('div');
    pickerContainer.className = 'room-mock-room-picker';
    pickerContainer.style.cssText = 'position:absolute;bottom:12px;right:12px;z-index:2;width:44px;height:44px;';

    // FAB button
    const fab = document.createElement('button');
    fab.className = 'room-mock-room-fab';
    fab.setAttribute('type', 'button');
    fab.setAttribute('aria-label', this.dataset.i18nRoomPicker || 'Room background picker');
    fab.style.cssText = 'position:absolute;bottom:0;right:0;width:44px;height:44px;border-radius:50%;border:none;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;background:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.18);z-index:1;color:#555;transition:transform 0.25s ease;';
    fab.innerHTML = SVG_ROOM;
    pickerContainer.appendChild(fab);

    // Room thumbnail pills
    const pills = [];
    const count = this.allRoomBackgrounds.length;
    const radius = 72;
    const arcStart = 180 * (Math.PI / 180);
    const arcEnd = 280 * (Math.PI / 180);

    this.allRoomBackgrounds.forEach((rb, i) => {
      const btn = document.createElement('button');
      btn.className = 'room-mock-room-pill' + (i === this.currentRoomIndex ? ' room-mock-room-pill--active' : '');
      btn.dataset.roomIndex = i;
      const roomLabel = (this.dataset.i18nRoomN || 'Room __N__').replace('__N__', String(i + 1));
      btn.setAttribute('aria-label', roomLabel);
      btn.setAttribute('type', 'button');

      const thumbUrl = this.getOptimizedImageUrl(rb.imageUrl, 80);
      const isActive = i === this.currentRoomIndex;

      // Compute thumbnail background to match the square-cropped view
      let bgSize = 'cover';
      let bgPos = 'center';
      if (rb.squareCrop) {
        let cx = rb.squareCrop.centerX ?? 0.5;
        let cy = rb.squareCrop.centerY ?? 0.5;
        let sz = rb.squareCrop.size ?? 1;
        if (cx <= 1) cx *= 100;
        if (cy <= 1) cy *= 100;
        if (sz <= 1) sz *= 100;
        const imgAspect = (rb.imageWidth || 1600) / (rb.imageHeight || 1000);
        const zoom = 100 / sz;
        bgSize = `${(imgAspect * zoom * 100).toFixed(0)}% ${(zoom * 100).toFixed(0)}%`;
        bgPos = `${cx.toFixed(1)}% ${cy.toFixed(1)}%`;
      }

      btn.style.cssText = `position:absolute;bottom:4px;right:4px;width:36px;height:36px;border-radius:50%;cursor:pointer;padding:0;background-image:url('${thumbUrl}');background-size:${bgSize};background-position:${bgPos};border:2.5px solid ${isActive ? '#6366f1' : 'transparent'};box-shadow:0 1px 3px rgba(0,0,0,0.2);transform:scale(0);opacity:0;z-index:0;transition:transform 0.25s cubic-bezier(0.34,1.56,0.64,1),opacity 0.2s ease,border-color 0.15s;transition-delay:0ms;`;

      // Pre-compute expanded position
      const angle = count > 1
        ? arcStart + (arcEnd - arcStart) * (i / (count - 1))
        : arcStart;
      btn._expandX = Math.cos(angle) * radius;
      btn._expandY = Math.sin(angle) * radius;

      pickerContainer.appendChild(btn);
      pills.push(btn);
    });

    // Toggle picker open/close
    fab.addEventListener('click', (e) => {
      e.stopPropagation();
      this._roomPickerOpen = !this._roomPickerOpen;
      if (this._roomPickerOpen) {
        fab.innerHTML = SVG_CLOSE;
        fab.style.transform = 'rotate(180deg)';
        pills.forEach((btn, i) => {
          btn.style.transitionDelay = (i * 25) + 'ms';
          btn.style.transform = `translate(${btn._expandX}px, ${btn._expandY}px) scale(1)`;
          btn.style.opacity = '1';
        });
      } else {
        fab.innerHTML = SVG_ROOM;
        fab.style.transform = 'rotate(0deg)';
        pills.forEach((btn, i) => {
          btn.style.transitionDelay = ((count - 1 - i) * 25) + 'ms';
          btn.style.transform = 'translate(0px, 0px) scale(0)';
          btn.style.opacity = '0';
        });
      }
    });

    // Room pill click
    pickerContainer.addEventListener('click', (e) => {
      const btn = e.target.closest('.room-mock-room-pill');
      if (!btn || btn.dataset.roomIndex === undefined) return;
      const index = parseInt(btn.dataset.roomIndex, 10);
      this._preferredRoomIndex = index;
      this._userPickedRoom = true;
      this._isAutoSwitchedRoom = false;
      this.switchRoom(index);
      // Auto-close palette
      setTimeout(() => {
        if (this._roomPickerOpen) fab.click();
      }, 150);
    });

    this._roomPills = pills;
    this._roomFab = fab;
    this._roomPickerContainer = pickerContainer;
    this.appendChild(pickerContainer);
  }

  /**
   * Switch to a different room background.
   * Uses canvas crossfade instead of DOM overlay.
   */
  switchRoom(index) {
    if (index === this.currentRoomIndex) return;
    if (!this.allRoomBackgrounds || index < 0 || index >= this.allRoomBackgrounds.length) return;

    this.currentRoomIndex = index;
    const roomBg = this.allRoomBackgrounds[index];

    // Update data attributes
    this.dataset.wallArea = JSON.stringify(roomBg.wallArea || { x: 20, y: 20, width: 60, height: 60 });
    this.dataset.squareCrop = JSON.stringify(roomBg.squareCrop || null);
    this.dataset.realWallWidth = String(roomBg.realWallWidth || 100);
    this.dataset.lightingOpacity = String(roomBg.lightingOpacity || 0);
    this.dataset.lightingBlend = roomBg.lightingBlend || 'multiply';

    const imgWidth = roomBg.imageWidth || 1600;
    const imgHeight = roomBg.imageHeight || 1000;
    this.dataset.imgAspect = String(imgWidth / imgHeight);

    // Refresh room-specific properties
    this.wallArea = this.parseJsonAttr(this.dataset.wallArea, { x: 20, y: 20, width: 60, height: 60 });
    this.squareCrop = this.parseJsonAttr(this.dataset.squareCrop, null);
    this.realWallWidth = parseFloat(this.dataset.realWallWidth) || 100;
    this.imgAspect = parseFloat(this.dataset.imgAspect) || 1.6;
    this.lightingOpacity = parseFloat(this.dataset.lightingOpacity) || 0;
    this.lightingBlend = this.dataset.lightingBlend || 'multiply';

    // Canvas crossfade: store old image, load new one, animate alpha
    const newSrc = this.getOptimizedImageUrl(roomBg.imageUrl, 1200);
    this._crossfadeFrom = this._roomBgImage;
    this._crossfadeAlpha = 0;
    this._crossfadeStart = performance.now();
    this._bgCacheKey = null; // Invalidate background cache for new room

    loadImage(newSrc).then(img => {
      if (img) {
        this._roomBgImage = img;
        this._animateCrossfade();
      }
    });

    // Render immediately with old image (frame positions update)
    this.scheduleRender();

    // Update pill active states
    if (this._roomPills) {
      this._roomPills.forEach((pill, i) => {
        pill.style.borderColor = i === index ? '#6366f1' : 'transparent';
        pill.classList.toggle('room-mock-room-pill--active', i === index);
      });
    }

    // Persist selection (skip for automatic room switches)
    if (!this._isAutoSwitching) {
      try {
        localStorage.setItem('room-background-selection', String(index));
      } catch { /* localStorage unavailable */ }
    }

    // Broadcast for cross-instance sync
    document.dispatchEvent(new CustomEvent('room-background:changed', {
      detail: { index, source: this, isAutoSwitch: !!this._isAutoSwitching }
    }));

    // Notify media-gallery that room background changed (for thumbnail sync)
    document.dispatchEvent(new CustomEvent('room-background:ready', {
      detail: { roomBg, source: this },
    }));
  }

  _animateCrossfade() {
    const elapsed = performance.now() - this._crossfadeStart;
    this._crossfadeAlpha = Math.min(1, elapsed / 350);
    this.render();
    if (this._crossfadeAlpha < 1) {
      requestAnimationFrame(() => this._animateCrossfade());
    } else {
      this._crossfadeFrom = null;
    }
  }

  /**
   * Handle room background change broadcast from another instance.
   */
  handleRoomChange(event) {
    const { index, source, isAutoSwitch } = event.detail || {};
    if (source === this) return;
    if (typeof index !== 'number' || !this.allRoomBackgrounds) return;
    if (index >= 0 && index < this.allRoomBackgrounds.length && index !== this.currentRoomIndex) {
      if (isAutoSwitch) {
        this._switchRoomAutomatic(index);
      } else {
        this._preferredRoomIndex = index;
        this._userPickedRoom = true;
        this._isAutoSwitchedRoom = false;
        this.switchRoom(index);
      }
    }
  }

  computeRoomFitRatio(roomIndex) {
    const room = this.allRoomBackgrounds[roomIndex];
    if (!room) return null;
    const realWallWidth = room.realWallWidth || 100;
    if (!this.layoutWidth || realWallWidth <= 0) return null;
    const longestSide = Math.max(this.layoutWidth, this._layoutHeight || 0);
    return longestSide / realWallWidth;
  }

  getAutoSwitchTarget() {
    if (!this.allRoomBackgrounds || this.allRoomBackgrounds.length <= 1) return -1;
    if (this._personalRoomBg) return -1;

    if (this._userPickedRoom) {
      const ratio = this.computeRoomFitRatio(this._preferredRoomIndex);
      if (ratio !== null && ratio >= ROOM_FIT_MIN_RATIO && ratio <= ROOM_FIT_MAX_RATIO) return -1;
    }

    const idealRatio = Math.sqrt(ROOM_FIT_MIN_RATIO * ROOM_FIT_MAX_RATIO);
    let bestIndex = -1;
    let bestDist = Infinity;

    for (let i = 0; i < this.allRoomBackgrounds.length; i++) {
      const ratio = this.computeRoomFitRatio(i);
      if (ratio === null) continue;
      const dist = Math.abs(ratio - idealRatio);
      if (dist < bestDist) {
        bestDist = dist;
        bestIndex = i;
      }
    }

    return (bestIndex >= 0 && bestIndex !== this.currentRoomIndex) ? bestIndex : -1;
  }

  maybeAutoSwitchRoom() {
    const target = this.getAutoSwitchTarget();
    if (target >= 0) {
      this._switchRoomAutomatic(target);
    }
  }

  _switchRoomAutomatic(index) {
    if (index === this.currentRoomIndex) return;
    this._isAutoSwitching = true;
    this.switchRoom(index);
    this._isAutoSwitching = false;
    this._isAutoSwitchedRoom = (index !== this._preferredRoomIndex);
  }

  /**
   * Build HTML structure for dynamically-created instances (cart preview, fetch modes)
   */
  buildHtmlStructure(roomBg, imgWidth, imgHeight) {
    this.style.aspectRatio = '1';
    this.innerHTML = `<div class="room-background-mock__container">
      <canvas class="room-background-mock__canvas"></canvas>
    </div>`;
  }

  /**
   * Apply room data from an external source (e.g. when a sibling instance
   * finishes fetching). Rebuilds the HTML and re-renders.
   */
  applyRoom(roomBg) {
    if (!roomBg?.imageUrl) return;
    const imgWidth = roomBg.imageWidth || 1600;
    const imgHeight = roomBg.imageHeight || 1000;

    this.dataset.wallArea = JSON.stringify(roomBg.wallArea || { x: 20, y: 20, width: 60, height: 60 });
    this.dataset.squareCrop = JSON.stringify(roomBg.squareCrop || null);
    this.dataset.realWallWidth = String(roomBg.realWallWidth || 100);
    this.dataset.lightingOpacity = String(roomBg.lightingOpacity || 0);
    this.dataset.lightingBlend = roomBg.lightingBlend || 'multiply';
    this.dataset.imgAspect = String(imgWidth / imgHeight);
    this.dataset.roomImageUrl = this.getOptimizedImageUrl(roomBg.imageUrl, 1200);

    this.classList.remove('room-background-mock--plain-wall');
    this.buildHtmlStructure(roomBg, imgWidth, imgHeight);
    this.parseData();
    this._initCanvas();
    this._loadRoomBackgroundImage();
    this._preloadArtworks();
    this.loadTexture();
  }

  /**
   * Get optimized image URL for Shopify CDN
   */
  getOptimizedImageUrl(url, size) {
    if (!url) return '';
    if (url.includes('cdn.shopify.com') || url.includes('shopify.dev')) {
      return url.includes('?') ? `${url}&width=${size}` : `${url}?width=${size}`;
    }
    return url;
  }

  /**
   * Extract frame configuration from layout based on config handle
   */
  extractFrameConfig(layout, configHandle, positionSize) {
    const defaults = {
      frameColor: '#2d2d2d',
      mouldingTile: null,
      mouldingWidth: 15,
      borderWidth: 0,
      borderOption: 'no-border',
      hasFrame: true
    };

    if (!layout) return defaults;

    const configurations = layout.configurations || [];
    let config = configurations.find(c => c.handle === configHandle);
    if (!config && configurations.length > 0) {
      config = configurations[0];
    }

    if (!config) return defaults;

    const frameMapping = config.frameMapping || config.frame_mapping || {};
    const firstPosKey = Object.keys(frameMapping)[0];
    const firstPosSettings = firstPosKey ? frameMapping[firstPosKey] : {};

    const framingOption = firstPosSettings._framing_option || firstPosSettings.framing_option || 'classic-frame';
    const frameColorHandle = firstPosSettings._frame_color || firstPosSettings.frame_color || firstPosSettings.frameColor;
    const borderOption = firstPosSettings._border_option || firstPosSettings.border_option || 'no-border';

    const hasFrame = !framingOption.includes('unframed') && !framingOption.includes('no-frame');

    let borderWidth = 0;
    if (borderOption.includes('mount')) {
      const pw = parseFloat(positionSize?.width) || 16;
      const ph = parseFloat(positionSize?.height) || 20;
      borderWidth = getMountWidthCm(pw, ph);
    } else if (borderOption.includes('border') && borderOption !== 'no-border') {
      borderWidth = 2.0;
    }

    const frameColors = layout.configuratorOptions?.frameColors || [];
    let matchedColor = frameColors.find(c => (c.handle || c.system?.handle) === frameColorHandle);
    if (!matchedColor && frameColors.length > 0) {
      matchedColor = frameColors[0];
    }

    let frameColorHex = defaults.frameColor;
    let mouldingTile = null;
    let mouldingWidth = defaults.mouldingWidth;

    if (matchedColor) {
      frameColorHex = matchedColor.hex || matchedColor.color || frameColorHex;
      mouldingWidth = matchedColor.mouldingWidth || matchedColor.moulding_width || mouldingWidth;

      const tileRaw = matchedColor.mouldingTile || matchedColor.moulding_tile;
      if (tileRaw) {
        if (typeof tileRaw === 'string' && (tileRaw.includes('http') || tileRaw.includes('//'))) {
          mouldingTile = tileRaw;
        } else if (tileRaw.src) {
          mouldingTile = tileRaw.src;
        }
      }
    }

    return {
      frameColor: frameColorHex,
      mouldingTile,
      mouldingWidth,
      borderWidth,
      hasFrame,
      borderOption
    };
  }

  /**
   * Render fallback when layout can't be loaded
   */
  renderFallback() {
    const artworkUrlsStr = this.dataset.artworkUrls || '';
    const artworkUrls = artworkUrlsStr.split('|||').filter(url => url && url.trim());

    if (artworkUrls.length === 0) {
      this.innerHTML = `
        <div class="room-background-mock__fallback" style="
          width: 100%;
          aspect-ratio: 1;
          background: #f0f0f0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: inherit;
          color: #999;
          font-size: 12px;
        ">
          Gallery Wall
        </div>
      `;
      return;
    }

    const cols = Math.ceil(Math.sqrt(artworkUrls.length));
    const rows = Math.ceil(artworkUrls.length / cols);

    this.innerHTML = `
      <div class="room-background-mock__fallback-grid" style="
        width: 100%;
        aspect-ratio: 1;
        display: grid;
        grid-template-columns: repeat(${cols}, 1fr);
        grid-template-rows: repeat(${rows}, 1fr);
        gap: 2px;
        background: #e0e0e0;
        padding: 4px;
        border-radius: inherit;
        box-sizing: border-box;
      ">
        ${artworkUrls.map((url, i) => `
          <img
            src="${this.getOptimizedImageUrl(url, 80)}"
            alt="Artwork ${i + 1}"
            style="
              width: 100%;
              height: 100%;
              object-fit: cover;
              border-radius: 2px;
            "
            loading="lazy"
          >
        `).join('')}
      </div>
    `;
  }

  /* ── Statement Pair Diptych Mode ── */

  _cacheRelatedProducts(event) {
    const { products } = event.detail || {};
    if (!products || !Array.isArray(products)) return;

    this._similarArtImages = products
      .map(p => p.image_url || p.imageUrl || '')
      .filter(url => url);

    if (this._diptychActive && this._similarArtImages.length) {
      if (!this.artworks[1]?.url) {
        this.artworks[1] = { url: this._similarArtImages[0], title: 'Similar artwork' };
        loadImage(this._similarArtImages[0]).then(() => this.scheduleRender());
      }
      if (!this._diptychCycleTimer && this._similarArtImages.length > 1) {
        this._startDiptychCycle(this._similarArtImages);
      }
    }
  }

  _getSimilarArtFromDOM() {
    if (this._similarArtImages?.length) return this._similarArtImages;

    const images = [];
    document.querySelectorAll('tags-discovery-grid .product-card__image, tags-discovery-grid .mock-image__artwork, related-products .product-card__image, related-products .mock-image__artwork').forEach(img => {
      const src = img.getAttribute('src');
      if (src) images.push(src.split('?')[0]);
    });

    if (images.length) this._similarArtImages = images;
    return images;
  }

  handleStatementPairToggle(event) {
    const { active, widthInches, heightInches } = event.detail || {};
    if (active) {
      this._enterDiptychMode(widthInches, heightInches);
    } else {
      this._exitDiptychMode();
    }
  }

  _enterDiptychMode(widthInches, heightInches) {
    const pos0 = this.positions[0];
    if (!pos0) return;

    this._originalPositions = [...this.positions];
    this._originalArtworks = [...this.artworks];
    this._originalLayoutWidth = this.layoutWidth;
    this._diptychActive = true;
    this._diptychArtIndex = 0;

    const W = widthInches || pos0.width;
    const H = heightInches || pos0.height;
    const GAP = 4;

    this.positions = [
      { x: 0, y: 0, width: W, height: H },
      { x: W + GAP, y: 0, width: W, height: H }
    ];
    this.layoutWidth = 2 * W + GAP;
    this.dataset.layoutWidth = String(this.layoutWidth);

    const similarImages = this._getSimilarArtFromDOM();
    const secondUrl = similarImages.length ? similarImages[0] : '';

    this.artworks = [
      this.artworks[0] || { url: '', title: '' },
      { url: secondUrl, title: 'Similar artwork' }
    ];

    if (secondUrl) {
      loadImage(secondUrl).then(() => this.scheduleRender());
    }
    this.scheduleRender();

    if (similarImages.length > 1) {
      this._startDiptychCycle(similarImages);
    }
  }

  _exitDiptychMode() {
    if (!this._diptychActive) return;

    if (this._diptychCycleTimer) {
      clearInterval(this._diptychCycleTimer);
      this._diptychCycleTimer = null;
    }

    this._diptychActive = false;

    if (this._originalPositions) {
      this.positions = this._originalPositions;
      this._originalPositions = null;
    }
    if (this._originalArtworks) {
      this.artworks = this._originalArtworks;
      this._originalArtworks = null;
    }
    if (this._originalLayoutWidth) {
      this.layoutWidth = this._originalLayoutWidth;
      this.dataset.layoutWidth = String(this.layoutWidth);
      this._originalLayoutWidth = null;
    }

    this.scheduleRender();
  }

  _startDiptychCycle(images) {
    if (this._diptychCycleTimer) clearInterval(this._diptychCycleTimer);

    this._diptychArtIndex = 0;

    this._diptychCycleTimer = setInterval(() => {
      if (!this._diptychActive) {
        clearInterval(this._diptychCycleTimer);
        this._diptychCycleTimer = null;
        return;
      }

      this._diptychArtIndex = (this._diptychArtIndex + 1) % images.length;
      const nextUrl = images[this._diptychArtIndex];

      if (this.artworks[1]) {
        this.artworks[1].url = nextUrl;
      }

      // Preload then render
      loadImage(nextUrl).then(() => this.scheduleRender());
    }, 4000);
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this._contentObserver) {
      this._contentObserver.disconnect();
    }
    if (this.configChangeHandler) {
      document.removeEventListener('gallery-wall:config-changed', this.configChangeHandler);
    }
    if (this.roomFitHandler) {
      document.removeEventListener('room-fit:background-applied', this.roomFitHandler);
    }
    if (this.roomChangeHandler) {
      document.removeEventListener('room-background:changed', this.roomChangeHandler);
    }
    if (this._statementPairHandler) {
      document.removeEventListener('statement-pair:toggled', this._statementPairHandler);
    }
    if (this._relatedProductsHandler) {
      document.removeEventListener('related-products:loaded', this._relatedProductsHandler);
    }
    if (this._diptychCycleTimer) {
      clearInterval(this._diptychCycleTimer);
      this._diptychCycleTimer = null;
    }
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    if (this._visibilityObserver) {
      this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
    }
  }

  _isRoomVisible() {
    const rect = this.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 &&
      rect.bottom > 0 && rect.top < window.innerHeight &&
      rect.right > 0 && rect.left < window.innerWidth;
  }

  _ensureVisibilityObserver() {
    if (this._visibilityObserver) return;
    this._visibilityObserver = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && this._pendingConfigEvent) {
        const evt = this._pendingConfigEvent;
        this._pendingConfigEvent = null;
        this.handleConfigChange(evt);
      }
    }, { threshold: 0 });
    this._visibilityObserver.observe(this);
  }

  handleConfigChange(event) {
    const { frameColorHex, frameColor: frameColorDirect, mouldingTile, mouldingWidth, borderWidth, borderOption, hasFrame, positions, wallDimensions, positionId, frameConfigs, isInitialization, productType } = event.detail || {};
    const frameColor = frameColorHex || frameColorDirect;

    if (positionId !== undefined) {
      return;
    }

    const isInsideGallery = this.closest('gallery-wall-media-gallery') !== null;

    // Only defer expensive size-change events when not visible.
    // Frame color/style changes are cheap and should apply immediately
    // to avoid stale wallDimensions being replayed later.
    if (!this._isRoomVisible() && wallDimensions) {
      this._pendingConfigEvent = event;
      this._ensureVisibilityObserver();
      return;
    }

    let needsTextureReload = false;

    // Handle initialization with per-position frame configs
    if (frameConfigs && typeof frameConfigs === 'object') {
      const currentTiles = new Set();
      const newTiles = new Set();

      if (this.mouldingTile) currentTiles.add(this.mouldingTile);
      for (const posId in this.frameConfigs) {
        if (this.frameConfigs[posId]?.mouldingTile) {
          currentTiles.add(this.frameConfigs[posId].mouldingTile);
        }
      }

      for (const posId in frameConfigs) {
        const config = frameConfigs[posId];
        if (config?.mouldingTile) {
          newTiles.add(config.mouldingTile);
        }
      }

      const tilesChanged = newTiles.size !== currentTiles.size ||
        [...newTiles].some(t => !currentTiles.has(t));

      this.frameConfigs = frameConfigs;

      // Also apply positions and wallDimensions if provided (init event includes both)
      if (positions && Array.isArray(positions) && positions.length > 0) {
        this.positions = positions;
      }
      if (wallDimensions && wallDimensions.widthInches) {
        this.layoutWidth = wallDimensions.widthInches;
        this.dataset.layoutWidth = String(wallDimensions.widthInches);
        this._layoutHeight = wallDimensions.heightInches || null;
      }

      if (tilesChanged) {
        this.loadTexture();
      } else {
        this.scheduleRender();
      }

      console.log('[room-background-mock] Applied frame configs from initialization event');
      return;
    }

    // Update positions if provided (size change)
    if (positions && Array.isArray(positions) && positions.length > 0) {
      this.positions = positions;
    }

    // Update layout width from dimensions if provided (size change)
    if (wallDimensions && wallDimensions.widthInches) {
      this.layoutWidth = wallDimensions.widthInches;
      this.dataset.layoutWidth = String(wallDimensions.widthInches);
      this._layoutHeight = wallDimensions.heightInches || null;

      if (!positions && this.positions.length === 1) {
        const newPositions = [{
          x: 0, y: 0,
          width: wallDimensions.widthInches,
          height: wallDimensions.heightInches
        }];
        this.positions = newPositions;
        this.dataset.positions = JSON.stringify(newPositions);
      }

      // Auto-switch room if print no longer fits — execute immediately
      const autoSwitchTarget = this.getAutoSwitchTarget();
      if (autoSwitchTarget >= 0) {
        // Render at new size first, then switch room
        this.scheduleRender();
        requestAnimationFrame(() => this._switchRoomAutomatic(autoSwitchTarget));
        return;
      }
    }

    if (frameColor) {
      this.frameColor = frameColor;
    }

    if (mouldingTile !== undefined) {
      const newMouldingTile = (mouldingTile && mouldingTile !== 'null' && mouldingTile !== '') ? mouldingTile : null;
      if (newMouldingTile !== this.mouldingTile) {
        this.mouldingTile = newMouldingTile;
        needsTextureReload = true;
      }
    }

    if (typeof mouldingWidth === 'number') {
      this.mouldingWidthMm = mouldingWidth;
    }

    if (typeof borderWidth === 'number') {
      this.borderWidthCm = borderWidth;
    }

    if (borderOption) {
      this.borderOption = borderOption;
    }

    if (typeof hasFrame === 'boolean') {
      this.hasFrame = hasFrame;
    }

    if (productType) {
      this.productType = productType;
    }

    if (needsTextureReload) {
      this.loadTexture();
    } else {
      this.scheduleRender();
    }
  }

  /**
   * Handle AI Room Fit personal background.
   */
  handleRoomFitBackground(event) {
    const { imageUrl, wallBoundingBox, roomType, wallDimensions } = event.detail || {};
    if (!imageUrl) return;

    this._personalRoomBg = { imageUrl, wallBoundingBox, roomType, wallDimensions };

    // Load the new room background image
    this._bgCacheKey = null; // Invalidate background cache
    loadImage(imageUrl).then(img => {
      if (img) {
        this._roomBgImage = img;
        this.scheduleRender();
      }
    });

    if (wallBoundingBox && typeof wallBoundingBox.x === 'number') {
      this.wallArea = {
        x: wallBoundingBox.x,
        y: wallBoundingBox.y,
        width: wallBoundingBox.width,
        height: wallBoundingBox.height,
      };
    }

    if (wallDimensions?.estimatedWidthFeet) {
      this.realWallWidth = wallDimensions.estimatedWidthFeet * 12;
    }

    console.log('[room-background-mock] Applied AI Room Fit personal background');
  }

  parseData() {
    this.wallArea = this.parseJsonAttr(this.dataset.wallArea, { x: 20, y: 20, width: 60, height: 60 });
    if (typeof this.wallArea !== 'object' || this.wallArea === null ||
        typeof this.wallArea.width !== 'number' || typeof this.wallArea.height !== 'number') {
      console.warn('[RoomBackgroundMock] Invalid wallArea, using default:', this.wallArea);
      this.wallArea = { x: 20, y: 20, width: 60, height: 60 };
    }

    this.positions = this.parseJsonAttr(this.dataset.positions, []);
    if (!Array.isArray(this.positions)) {
      console.warn('[RoomBackgroundMock] Invalid positions, using default:', this.positions);
      this.positions = [];
    }

    this.artworks = this.parseJsonAttr(this.dataset.artworks, []);
    if (!Array.isArray(this.artworks)) this.artworks = [];

    this.squareCrop = this.parseJsonAttr(this.dataset.squareCrop, null);

    this.realWallWidth = parseFloat(this.dataset.realWallWidth) || 100;
    // Calculate layoutWidth: prefer explicit attribute, then derive from positions,
    // fallback to realWallWidth. Without correct layoutWidth the frames overflow the mock.
    const explicitLayoutWidth = parseFloat(this.dataset.layoutWidth);
    if (explicitLayoutWidth) {
      this.layoutWidth = explicitLayoutWidth;
    } else if (this.positions.length > 0) {
      // Derive from rightmost frame edge (positions are in inches)
      const rightEdge = Math.max(...this.positions.map(p => (p.x || 0) + (p.width || 0)));
      const leftEdge = Math.min(...this.positions.map(p => p.x || 0));
      this.layoutWidth = rightEdge + leftEdge; // symmetric padding
    } else {
      this.layoutWidth = this.realWallWidth;
    }
    this.lightingOpacity = parseFloat(this.dataset.lightingOpacity) || 0;
    this.lightingBlend = this.dataset.lightingBlend || 'multiply';
    this.imgAspect = parseFloat(this.dataset.imgAspect) || 1.6;

    this.frameColor = this.dataset.frameColor || '#2d2d2d';
    const tileValue = this.dataset.mouldingTile;
    this.mouldingTile = (tileValue && tileValue !== 'null' && tileValue !== '') ? tileValue : null;
    this.mouldingWidthMm = parseFloat(this.dataset.mouldingWidth) || 15;
    this.borderWidthCm = parseFloat(this.dataset.borderWidth) || 0;
    this.borderOption = this.dataset.borderOption || 'no-border';
    this.hasFrame = this.dataset.hasFrame !== 'false';

    this.frameConfigs = this.parseJsonAttr(this.dataset.frameConfigs, {});
    if (typeof this.frameConfigs !== 'object' || this.frameConfigs === null) this.frameConfigs = {};

    this.frameDepth = parseFloat(this.dataset.frameDepth) || 0.5;
    this.glassReflection = parseFloat(this.dataset.glassReflection) || 0.06;
    this.productType = this.dataset.productType || 'art_print';
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleRender();
    });
    const container = this.querySelector('.room-background-mock__container');
    if (container) {
      this.resizeObserver.observe(container);
    }
  }

  loadTexture() {
    const textureUrls = new Set();
    if (this.mouldingTile && this.hasFrame) {
      textureUrls.add(this.mouldingTile);
    }
    for (const posId in this.frameConfigs) {
      const config = this.frameConfigs[posId];
      if (config.mouldingTile && config.hasFrame !== false) {
        textureUrls.add(config.mouldingTile);
      }
    }

    if (textureUrls.size === 0) {
      this.textureLoaded = false;
      this.scheduleRender();
      return;
    }

    let loadedCount = 0;
    const totalCount = textureUrls.size;

    textureUrls.forEach(url => {
      loadImage(url).then(() => {
        loadedCount++;
        if (loadedCount === totalCount) {
          this.textureLoaded = true;
          this.scheduleRender();
        }
      });
    });
  }

  /* ══════════════════════════════════════════════════════
   *  CANVAS RENDERING PIPELINE
   * ══════════════════════════════════════════════════════ */

  scheduleRender() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      this.render();
    });
  }

  render() {
    if (!this.canvas || !this.ctx || !this._roomBgImage) return;

    const rect = this.getBoundingClientRect();
    const displayW = rect.width || 400;
    const displayH = rect.height || 400;
    const maxDpr = 2;
    const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
    const w = Math.round(displayW * dpr);
    const h = Math.round(displayH * dpr);

    // Only reset canvas dimensions when they actually change (avoids GPU context reset)
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      // Invalidate background cache on resize
      this._bgCacheKey = null;
    }

    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);

    // 1. Draw room background (cached to offscreen canvas to avoid expensive
    //    drawImage on every frame during slider drags)
    if (this._crossfadeFrom && this._crossfadeAlpha < 1) {
      // During crossfade, draw both backgrounds directly (no cache)
      this.drawRoomBackground(ctx, w, h, this._crossfadeFrom);
      ctx.globalAlpha = this._crossfadeAlpha;
      this.drawRoomBackground(ctx, w, h, this._roomBgImage);
      ctx.globalAlpha = 1;
    } else {
      this._drawCachedBackground(ctx, w, h);
    }

    if (!this.positions.length) {
      this._cacheIfNeeded();
      return;
    }

    // 2. Calculate frame coordinates
    const coords = this._calculateFrameCoords(w, h);
    if (!coords) {
      this._cacheIfNeeded();
      return;
    }

    const { pxPerUnit, layoutLeft, layoutTop, bounds } = coords;

    // 3. Draw each frame position
    const artworkRects = [];

    for (let i = 0; i < this.positions.length; i++) {
      const pos = this.positions[i];
      const artwork = this.artworks[i] || null;
      const posConfig = this.getPositionConfig(pos, i);

      const result = this._drawFrameSlot(ctx, pos, artwork, posConfig, pxPerUnit, layoutLeft, layoutTop, bounds, i);
      if (result?.artworkRect) artworkRects.push(result.artworkRect);
    }

    // 4. Lighting overlay (clip to artwork rects, blend room bg onto artwork areas)
    if (this.lightingOpacity > 0 && artworkRects.length > 0) {
      this._drawLightingOverlay(ctx, w, h, artworkRects);
    }

    // 5. Debug overlay
    if (RoomBackgroundMock._debug ?? (RoomBackgroundMock._debug = new URLSearchParams(window.location.search).get('roomBackgroundDebug') === '1')) {
      this._drawDebugOverlay(ctx, w, h, coords);
    }

    this._cacheIfNeeded();

    // Now that the framed composite has rendered, upgrade the in-room
    // thumbnail from the plain room photo (painted by _prefetchRoomPhoto) to
    // the composite so the thumbnail shows the frame too.
    this._upgradeRoomThumbnail();
  }

  /**
   * Replace the plain room-photo background on the in-room thumbnails
   * ([data-room-thumb-photo]) with a snapshot of the rendered composite (room +
   * frame + artwork). Scoped to the deferred PDP main-slide instance (same
   * scope as _prefetchRoomPhoto) so related-product/cart room mocks don't paint
   * the PDP thumbnail. Debounced so size-slider drags snapshot once on settle.
   */
  _upgradeRoomThumbnail() {
    if (this.dataset.fetchRoom == null || !this.canvas || !this.positions.length) return;
    clearTimeout(this._thumbCompositeTimer);
    this._thumbCompositeTimer = setTimeout(() => {
      let css;
      try {
        css = `url("${this.canvas.toDataURL('image/jpeg', 0.72)}")`;
      } catch (e) {
        return; // tainted canvas — keep the plain room photo
      }
      document.querySelectorAll('[data-room-thumb-photo]').forEach((el) => {
        el.style.backgroundImage = css;
        el.style.backgroundSize = 'cover';
        el.style.backgroundPosition = 'center';
      });
    }, 180);
  }

  /**
   * Draw room background from offscreen cache.
   * The room background image is large (~1600px) and the square-crop drawImage call
   * is expensive. Caching to an offscreen canvas means we only pay for the crop once
   * per room/crop change, and subsequent frames (during slider drags etc.) use a fast
   * canvas-to-canvas blit.
   */
  _drawCachedBackground(ctx, w, h) {
    const crop = this.getEffectiveCrop();
    const cacheKey = `${this._roomBgImage.src}|${w}|${h}|${crop.centerX}|${crop.centerY}|${crop.size}`;

    if (this._bgCacheKey !== cacheKey) {
      // Create or resize offscreen canvas
      if (!this._bgCache || this._bgCache.width !== w || this._bgCache.height !== h) {
        this._bgCache = document.createElement('canvas');
        this._bgCache.width = w;
        this._bgCache.height = h;
      }
      const offCtx = this._bgCache.getContext('2d');
      this.drawRoomBackground(offCtx, w, h, this._roomBgImage);
      this._bgCacheKey = cacheKey;
    }

    ctx.drawImage(this._bgCache, 0, 0);
  }

  _cacheIfNeeded() {
    if (this._shouldCacheAfterRender) {
      this._shouldCacheAfterRender = false;
      const cacheKey = this.getCacheKey();
      renderedStateCache.set(cacheKey, {
        html: this.innerHTML,
        dataAttrs: { ...this.dataset },
        aspectRatio: this.style.aspectRatio
      });
    }
  }

  /**
   * Draw room background with square crop via drawImage source rect.
   */
  drawRoomBackground(ctx, displayW, displayH, img) {
    const crop = this.getEffectiveCrop();
    const AR = this.imgAspect || 1.6;
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return;

    const cropFrac = crop.size / 100;

    // Square source region from the image
    let sw, sh;
    if (AR >= 1) { sh = ih * cropFrac; sw = sh; }   // landscape: height constrains
    else         { sw = iw * cropFrac; sh = sw; }    // portrait: width constrains

    let sx = (crop.centerX / 100) * iw - sw / 2;
    let sy = (crop.centerY / 100) * ih - sh / 2;
    sx = Math.max(0, Math.min(sx, iw - sw));
    sy = Math.max(0, Math.min(sy, ih - sh));

    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, displayW, displayH);
  }

  /**
   * Calculate frame coordinates: wall area % → canvas pixels.
   * Collapses the 3-step SVG transform into one direct mapping.
   */
  _calculateFrameCoords(displayW, displayH) {
    const crop = this.getEffectiveCrop();
    const AR = this.imgAspect || 1.6;
    const cropScale = 100 / crop.size;
    const bgW = cropScale * Math.max(AR, 1);
    const bgH = cropScale * Math.max(1, 1 / AR);

    // Wall area mapped to canvas pixels
    const wa = this.wallArea;
    const wallLeft   = (50 + bgW * (wa.x - crop.centerX)) / 100 * displayW;
    const wallTop    = (50 + bgH * (wa.y - crop.centerY)) / 100 * displayH;
    const wallWidth  = (wa.width * bgW) / 100 * displayW;
    const wallHeight = (wa.height * bgH) / 100 * displayH;

    // Scale wall for layout-to-wall ratio (layout + frame padding)
    const frameInches = this.getFrameWidthInches();
    const outerW = this.layoutWidth + 2 * frameInches;
    const widthScale = Math.min(1, outerW / this.realWallWidth);
    const scaledWallW = wallWidth * widthScale;
    const scaledWallL = wallLeft + (wallWidth - scaledWallW) / 2;

    // Layout bounds in position units (includes frame padding)
    const bounds = this.calculateLayoutBounds();
    if (bounds.width <= 0 || bounds.height <= 0) return null;

    // pxPerUnit: uniform scale to fit layout within wall area (preserveAspectRatio meet)
    const pxPerUnit = Math.min(scaledWallW / bounds.width, wallHeight / bounds.height);
    const layoutPxW = bounds.width * pxPerUnit;
    const layoutPxH = bounds.height * pxPerUnit;
    const layoutLeft = scaledWallL + (scaledWallW - layoutPxW) / 2;
    const layoutTop = wallTop + (wallHeight - layoutPxH) / 2;

    return {
      pxPerUnit,
      layoutLeft,
      layoutTop,
      bounds,
      wallLeft,
      wallTop,
      wallWidth,
      wallHeight,
      scaledWallW,
      scaledWallL,
      widthScale
    };
  }

  _drawFrameSlot(ctx, pos, artwork, posConfig, pxPerUnit, layoutLeft, layoutTop, bounds, index) {
    const isCanvas = (posConfig.productType || this.productType) === 'canvas';
    if (isCanvas) {
      return this._drawCanvasFrameSlot(ctx, pos, artwork, posConfig, pxPerUnit, layoutLeft, layoutTop, bounds, index);
    } else {
      return this._drawArtPrintFrameSlot(ctx, pos, artwork, posConfig, pxPerUnit, layoutLeft, layoutTop, bounds, index);
    }
  }

  _drawArtPrintFrameSlot(ctx, pos, artwork, config, pxPerUnit, layoutLeft, layoutTop, bounds, index) {
    const MM_TO_INCHES = 0.03937;
    const CM_TO_INCHES = 0.3937;

    const posHasFrame = config.hasFrame !== undefined ? config.hasFrame : this.hasFrame;
    const posMouldingWidthMm = config.mouldingWidth ?? this.mouldingWidthMm;
    const frameWidthInches = posHasFrame ? (posMouldingWidthMm * MM_TO_INCHES) : 0;
    const frameWidthPx = frameWidthInches * (this.svgUnitsPerInch || 1.0) * pxPerUnit;

    const posBorderOption = config.borderOption || this.borderOption || '';
    const posBorderWidthCm = config.borderWidth ?? this.borderWidthCm;
    const effectiveBorderCm = posBorderOption === 'no-border' ? 0 : posBorderWidthCm;
    const borderWidthPx = effectiveBorderCm * CM_TO_INCHES * (this.svgUnitsPerInch || 1.0) * pxPerUnit;

    // Position in canvas pixels
    const posX = layoutLeft + (pos.x - bounds.minX) * pxPerUnit;
    const posY = layoutTop + (pos.y - bounds.minY) * pxPerUnit;
    const posW = pos.width * pxPerUnit;
    const posH = pos.height * pxPerUnit;

    // Frame outer rect
    const frameX = posX - frameWidthPx;
    const frameY = posY - frameWidthPx;
    const frameW = posW + frameWidthPx * 2;
    const frameH = posH + frameWidthPx * 2;

    // Artwork area (inset by border)
    const artX = posX + borderWidthPx;
    const artY = posY + borderWidthPx;
    const artW = posW - borderWidthPx * 2;
    const artH = posH - borderWidthPx * 2;

    const mouldingTile = config.mouldingTile !== undefined ? config.mouldingTile : this.mouldingTile;

    renderArtPrintFrame(ctx, {
      frame: posHasFrame ? { x: frameX, y: frameY, w: frameW, h: frameH } : null,
      opening: { x: posX, y: posY, w: posW, h: posH },
      artwork: { x: artX, y: artY, w: artW, h: artH },
      frameWidthPx, matWidthPx: borderWidthPx, hasFrame: posHasFrame,
      frameColor: config.frameColor || this.frameColor,
      frameDepth: this.frameDepth || 0.5,
      mouldingTexture: mouldingTile ? getCachedImage(mouldingTile) : null,
      artworkImage: artwork?.url ? getCachedImage(artwork.url) : null,
      glassReflection: this.glassReflection || 0,
      borderOption: posBorderOption,
      pxPerInch: pxPerUnit * (this.svgUnitsPerInch || 1.0),
      openingFill: '#e8e4dc',
    });

    return { artworkRect: { x: artX, y: artY, w: artW, h: artH } };
  }

  _drawCanvasFrameSlot(ctx, pos, artwork, config, pxPerUnit, layoutLeft, layoutTop, bounds, index) {
    const MM_TO_INCHES = 0.03937;
    const svgPerInch = this.svgUnitsPerInch || 1.0;

    const CANVAS_FRAME_WIDTH_MM = 12;
    const CANVAS_FRAME_DEPTH = 0.85;
    const CANVAS_FLOAT_GAP_MM = 8;
    const CANVAS_EDGE_MM = 5;

    const posHasFrame = config.hasFrame !== undefined ? config.hasFrame : this.hasFrame;
    const canvasEdgePx = CANVAS_EDGE_MM * MM_TO_INCHES * svgPerInch * pxPerUnit;
    const floatGapPx = posHasFrame ? CANVAS_FLOAT_GAP_MM * MM_TO_INCHES * svgPerInch * pxPerUnit : 0;
    const frameWidthPx = posHasFrame ? CANVAS_FRAME_WIDTH_MM * MM_TO_INCHES * svgPerInch * pxPerUnit : 0;

    // Position in canvas pixels
    const posX = layoutLeft + (pos.x - bounds.minX) * pxPerUnit;
    const posY = layoutTop + (pos.y - bounds.minY) * pxPerUnit;
    const posW = pos.width * pxPerUnit;
    const posH = pos.height * pxPerUnit;

    const totalExpansion = canvasEdgePx + floatGapPx + frameWidthPx;
    const outerX = posX - totalExpansion;
    const outerY = posY - totalExpansion;
    const outerW = posW + totalExpansion * 2;
    const outerH = posH + totalExpansion * 2;

    const edgeX = posX - canvasEdgePx;
    const edgeY = posY - canvasEdgePx;
    const edgeW = posW + canvasEdgePx * 2;
    const edgeH = posH + canvasEdgePx * 2;

    const mouldingTile = config.mouldingTile !== undefined ? config.mouldingTile : this.mouldingTile;

    renderCanvasProductFrame(ctx, {
      outer: { x: outerX, y: outerY, w: outerW, h: outerH },
      edge: { x: edgeX, y: edgeY, w: edgeW, h: edgeH },
      frameWidthPx, floatGapPx, canvasEdgePx, hasFrame: posHasFrame,
      frameColor: config.frameColor || this.frameColor,
      frameDepth: CANVAS_FRAME_DEPTH,
      mouldingTexture: mouldingTile ? getCachedImage(mouldingTile) : null,
      artworkImage: artwork?.url ? getCachedImage(artwork.url) : null,
      edgeFill: '#f5f5f5',
      textureOpacity: 0.55,
      borderLine: true,
      borderLineWidth: Math.max(0.5, 0.15 * pxPerUnit),
    });

    return { artworkRect: { x: posX, y: posY, w: posW, h: posH } };
  }

  /**
   * Lighting overlay: clip to artwork rects, blend room bg onto artwork areas.
   * Uses the configured lightingBlend mode (defaults to 'multiply').
   */
  _drawLightingOverlay(ctx, w, h, artworkRects) {
    ctx.save();

    // Build clip path from artwork rects
    ctx.beginPath();
    for (const r of artworkRects) {
      ctx.rect(r.x, r.y, r.w, r.h);
    }
    ctx.clip();

    // Blend room background over artwork areas using configured blend mode
    ctx.globalCompositeOperation = this.lightingBlend || 'multiply';
    ctx.globalAlpha = this.lightingOpacity;
    // Use cached background for performance
    if (this._bgCache) {
      ctx.drawImage(this._bgCache, 0, 0);
    } else {
      this.drawRoomBackground(ctx, w, h, this._roomBgImage);
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    ctx.restore();
  }

  /**
   * Debug overlay: draw coloured rects + text labels on canvas.
   */
  _drawDebugOverlay(ctx, w, h, coords) {
    const { wallLeft, wallTop, wallWidth, wallHeight, scaledWallW, scaledWallL, layoutLeft, layoutTop, bounds, pxPerUnit, widthScale } = coords;

    // Wall area box (cyan)
    ctx.strokeStyle = 'cyan';
    ctx.lineWidth = 2;
    ctx.strokeRect(wallLeft, wallTop, wallWidth, wallHeight);

    // Scaled wall area (red)
    ctx.strokeStyle = 'red';
    ctx.lineWidth = 2;
    const layoutPxW = bounds.width * pxPerUnit;
    const layoutPxH = bounds.height * pxPerUnit;
    ctx.strokeRect(layoutLeft, layoutTop, layoutPxW, layoutPxH);

    // Frame rects (yellow dashed)
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = 'yellow';
    for (const pos of this.positions) {
      const px = layoutLeft + (pos.x - bounds.minX) * pxPerUnit;
      const py = layoutTop + (pos.y - bounds.minY) * pxPerUnit;
      const pw = pos.width * pxPerUnit;
      const ph = pos.height * pxPerUnit;
      ctx.strokeRect(px, py, pw, ph);
    }
    ctx.setLineDash([]);

    // Labels
    ctx.font = 'bold 11px monospace';
    ctx.fillStyle = 'rgba(0,0,0,0.75)';
    const labelY = wallTop + 14;
    ctx.fillRect(wallLeft, wallTop, 260, 48);
    ctx.fillStyle = 'cyan';
    ctx.fillText(`WALL ${this.realWallWidth}" wide  room[${this.currentRoomIndex || 0}]`, wallLeft + 4, labelY);
    ctx.fillText(`${Math.round(wallWidth)}×${Math.round(wallHeight)}px  scale=${widthScale.toFixed(2)}`, wallLeft + 4, labelY + 14);
    ctx.fillStyle = 'yellow';
    ctx.fillText(`layout ${this.layoutWidth}"  pxPerUnit=${pxPerUnit.toFixed(2)}`, wallLeft + 4, labelY + 28);
  }

  /* ══════════════════════════════════════════════════════
   *  LAYOUT & GEOMETRY
   * ══════════════════════════════════════════════════════ */

  calculateLayoutBounds() {
    if (!this.positions.length) {
      return { minX: 0, minY: 0, width: 100, height: 100 };
    }

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

    for (const pos of this.positions) {
      minX = Math.min(minX, pos.x);
      minY = Math.min(minY, pos.y);
      maxX = Math.max(maxX, pos.x + pos.width);
      maxY = Math.max(maxY, pos.y + pos.height);
    }

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    if (this.layoutWidth > 0 && contentWidth > 0) {
      this.svgUnitsPerInch = contentWidth / this.layoutWidth;
    } else {
      this.svgUnitsPerInch = 1.0;
    }

    const frameWidth = this.getFrameWidth();
    minX -= frameWidth;
    minY -= frameWidth;
    maxX += frameWidth;
    maxY += frameWidth;

    return {
      minX,
      minY,
      width: maxX - minX,
      height: maxY - minY
    };
  }

  getFrameWidth() {
    if (!this.hasFrame) return 0;
    const MM_TO_INCHES = 0.03937;
    const DEFAULT_FRAME_WIDTH_INCHES = 1.5 * 0.3937;
    let frameWidthInches;
    if (this.mouldingWidthMm && this.mouldingWidthMm > 0) {
      frameWidthInches = this.mouldingWidthMm * MM_TO_INCHES;
    } else {
      frameWidthInches = DEFAULT_FRAME_WIDTH_INCHES;
    }
    return frameWidthInches * (this.svgUnitsPerInch || 1.0);
  }

  getFrameWidthInches() {
    if (!this.hasFrame) return 0;
    const MM_TO_INCHES = 0.03937;
    const CLASSIC_FRAME_WIDTH_INCHES = 1.5 * 0.3937;
    return this.mouldingWidthMm ? (this.mouldingWidthMm * MM_TO_INCHES) : CLASSIC_FRAME_WIDTH_INCHES;
  }

  getBorderWidth() {
    const CM_TO_INCHES = 0.3937;
    const borderWidthInches = this.borderWidthCm * CM_TO_INCHES;
    return borderWidthInches * (this.svgUnitsPerInch || 1.0);
  }

  getBorderWidthInches() {
    return this.borderWidthCm * 0.3937;
  }

  findPositionIndex(positionId) {
    if (positionId === undefined) return -1;
    const posIdStr = String(positionId);

    const byId = this.positions.findIndex(p => {
      const pid = p.id ?? p.positionId;
      return pid !== undefined && String(pid) === posIdStr;
    });
    if (byId >= 0) return byId;

    const numId = parseInt(posIdStr, 10);
    if (!isNaN(numId) && numId >= 1 && numId <= this.positions.length) {
      return numId - 1;
    }

    return -1;
  }

  getPositionConfig(pos, index) {
    const posId = pos.id ?? pos.positionId;
    let config = {};

    if (posId !== undefined) {
      config = this.frameConfigs[posId] || this.frameConfigs[String(posId)] || {};
    } else {
      const oneIndexedId = index + 1;
      config =
        this.frameConfigs[String(oneIndexedId)] ||
        this.frameConfigs[oneIndexedId] ||
        this.frameConfigs[String(index)] ||
        this.frameConfigs[index] ||
        {};
    }

    return {
      frameColor: config.frameColor || this.frameColor,
      mouldingTile: config.mouldingTile !== undefined ? config.mouldingTile : this.mouldingTile,
      mouldingWidth: config.mouldingWidth ?? this.mouldingWidthMm,
      hasFrame: config.hasFrame !== undefined ? config.hasFrame : this.hasFrame,
      borderWidth: config.borderWidth ?? this.borderWidthCm,
      borderOption: config.borderOption || this.borderOption || 'no-border',
      productType: config.productType || this.productType || 'art_print'
    };
  }

  /**
   * Update configuration dynamically (called when variant/options change)
   */
  updateConfig(config) {
    if (!config) return;

    let needsTextureReload = false;
    let needsRender = false;

    const isSinglePositionUpdate = config.positionId !== undefined;

    if (isSinglePositionUpdate) {
      const posId = String(config.positionId);
      if (config.mouldingTile && !getCachedImage(config.mouldingTile)) {
        needsTextureReload = true;
      }
      this.frameConfigs[posId] = {
        ...this.frameConfigs[posId],
        frameColor: config.frameColor,
        mouldingTile: config.mouldingTile,
        mouldingWidth: config.mouldingWidth,
        hasFrame: config.hasFrame,
        borderWidth: config.borderWidth,
        borderOption: config.borderOption,
        productType: config.productType
      };
      this.dataset.frameConfigs = JSON.stringify(this.frameConfigs);
      if (!needsTextureReload) needsRender = true;
    } else {
      if (config.frameColor !== undefined && config.frameColor !== this.frameColor) {
        this.frameColor = config.frameColor;
        this.dataset.frameColor = config.frameColor;
        needsRender = true;
      }

      if (config.mouldingTile !== undefined) {
        const newTile = (config.mouldingTile && config.mouldingTile !== 'null' && config.mouldingTile !== '')
          ? config.mouldingTile : null;
        if (newTile !== this.mouldingTile) {
          this.mouldingTile = newTile;
          this.dataset.mouldingTile = newTile || '';
          needsTextureReload = true;
        }
      }

      if (config.hasFrame !== undefined && config.hasFrame !== this.hasFrame) {
        this.hasFrame = config.hasFrame;
        this.dataset.hasFrame = String(config.hasFrame);
        needsTextureReload = true;
      }

      if (config.borderWidth !== undefined && config.borderWidth !== this.borderWidthCm) {
        this.borderWidthCm = config.borderWidth;
        this.dataset.borderWidth = String(config.borderWidth);
        needsRender = true;
      }

      if (config.borderOption !== undefined && config.borderOption !== this.borderOption) {
        this.borderOption = config.borderOption;
        this.dataset.borderOption = config.borderOption;
        needsRender = true;
      }

      if (config.productType !== undefined && config.productType !== this.productType) {
        this.productType = config.productType;
        this.dataset.productType = config.productType;
        needsRender = true;
      }

      if (config.mouldingWidth !== undefined && config.mouldingWidth !== this.mouldingWidthMm) {
        this.mouldingWidthMm = config.mouldingWidth;
        this.dataset.mouldingWidth = String(config.mouldingWidth);
        needsRender = true;
      }

      if (config.frameConfigs) {
        for (const posId in config.frameConfigs) {
          const posConfig = config.frameConfigs[posId];
          if (posConfig.mouldingTile && !getCachedImage(posConfig.mouldingTile)) {
            needsTextureReload = true;
            break;
          }
        }
        this.frameConfigs = { ...this.frameConfigs, ...config.frameConfigs };
        this.dataset.frameConfigs = JSON.stringify(this.frameConfigs);
        if (!needsTextureReload) needsRender = true;
      }
    }

    // Update positions if provided (size change)
    if (config.positions && Array.isArray(config.positions) && config.positions.length > 0) {
      this.positions = config.positions;
      this.dataset.positions = JSON.stringify(config.positions);
      needsRender = true;
    }

    // Update layout width from wall dimensions if provided
    if (config.wallDimensions && config.wallDimensions.widthInches) {
      this.layoutWidth = config.wallDimensions.widthInches;
      this.dataset.layoutWidth = String(config.wallDimensions.widthInches);
      this._layoutHeight = config.wallDimensions.heightInches || null;

      if (!config.positions && this.positions.length === 1) {
        const newPositions = [{
          x: 0, y: 0,
          width: config.wallDimensions.widthInches,
          height: config.wallDimensions.heightInches
        }];
        this.positions = newPositions;
        this.dataset.positions = JSON.stringify(newPositions);
      }

      // Auto-switch room if print no longer fits
      const autoSwitchTarget = this.getAutoSwitchTarget();
      if (autoSwitchTarget >= 0) {
        this.scheduleRender();
        requestAnimationFrame(() => this._switchRoomAutomatic(autoSwitchTarget));
        return;
      }

      needsRender = true;
    }

    if (needsTextureReload) {
      this.loadTexture();
    } else if (needsRender) {
      this.scheduleRender();
    }
  }
}

// Register custom element
if (!customElements.get('room-background-mock')) {
  customElements.define('room-background-mock', RoomBackgroundMock);
}
